// ─── Écran PROJECTION : mois à venir, solde cumulé, trésorerie ────
// Troisième onglet du module (Cédric, 16/08, backlog CONCEPTION-FINANCE.md
// §7 incrément 3) : répond à « où en sera ma trésorerie dans N mois ? ».
// La trésorerie ACTUELLE (Σ des soldes de comptes, réel) sert d'ancrage ; le
// prévu de chaque ligne, mois par mois, s'y ajoute — `projectionMensuelle`
// fait tout le travail, ce composant ne fait qu'accumuler et afficher.
// Pas de détail par enveloppe ici : c'est le rôle du Suivi (Mois) pour le
// mois courant/passé. Les crédits (`calculerCredit`) sont un incrément à
// part, en attente des paramètres réels des prêts à ressaisir.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, SelecteurDuree, enMois, useUI, useT, useEspace,
} from '@mop/core';
import { ecouterComptes, soldeCompte } from './services/comptes';
import { ecouterOperations } from './services/budget';
import { ecouterLignes, ecouterExceptions } from './services/lignes';
import { projectionMensuelle } from './services/occurrences';
import { formatEuro } from './services/calculs';

const cent = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Même motif que les 11 autres écrans du module (`libelleMois` dupliqué
// plutôt qu'extrait — convention déjà en place, cf. `EcranFinances.js` &co).
const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function EcranProjection() {
  const UI = useUI();
  const t = useT();
  const { tenantId } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [comptes, setComptes] = useState([]);
  const [operations, setOperations] = useState([]);
  const [lignes, setLignes] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [erreur, setErreur] = useState('');
  // Horizon réglable au compteur ± (Cédric, 13/08 — même sélecteur partout).
  const [duree, setDuree] = useState({ n: 12, unite: 'mois' });
  const signaler = (quoi) => (e) => setErreur(`${quoi} — ${e?.message || e?.code || 'erreur'}`);

  useEffect(() => ecouterComptes(tenantId, setComptes, signaler('comptes')), [tenantId]);
  useEffect(() => ecouterOperations(tenantId, setOperations, signaler('operations')), [tenantId]);
  useEffect(() => ecouterLignes(tenantId, setLignes, signaler('lignesBudget')), [tenantId]);
  useEffect(() => ecouterExceptions(tenantId, setExceptions, signaler('occurrencesBudget')), [tenantId]);

  // Ancrage RÉEL — pas un prévu : la somme des soldes courants, comme
  // l'écran Comptes. La projection part de là, jamais de zéro.
  const tresorerieActuelle = useMemo(
    () => cent(comptes.reduce((s2, c) => s2 + soldeCompte(c, operations), 0)),
    [comptes, operations],
  );

  const horizon = enMois(duree);
  const mois = useMemo(
    () => projectionMensuelle({ lignes, exceptions, horizon: typeof horizon === 'number' ? horizon : 12 }),
    [lignes, exceptions, horizon],
  );

  // Solde cumulé : l'ancrage + le prévu de tous les mois jusqu'à celui-ci
  // inclus — un simple `reduce` local, pas une nouvelle fonction service.
  const lignesProjetees = useMemo(() => {
    let cumule = tresorerieActuelle;
    return mois.map(m => {
      cumule = cent(cumule + m.prevu);
      return { ...m, cumule };
    });
  }, [mois, tresorerieActuelle]);

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('finances.ongletProjection')} iconeTitre="📈" />

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <View style={s.erreur}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </View>
        )}

        <View style={s.totalCarte}>
          <Text style={s.totalLbl}>{t('finances.tresorerieActuelle')}</Text>
          <Text style={[s.totalVal, tresorerieActuelle < 0 && { color: UI.danger || '#c0392b' }]}>
            {formatEuro(tresorerieActuelle)}
          </Text>
        </View>

        <View style={s.dureeLigne}>
          <Text style={s.dureeLbl}>{t('finances.soldeProjete')}</Text>
          <SelecteurDuree valeur={duree} onChange={setDuree} maxMois={36} min={1} />
        </View>

        {lignes.length === 0 ? (
          <EmptyState icon="📈" texte={t('finances.aucunePrevision')} aide={t('finances.aucunePrevisionAide')} />
        ) : (
          lignesProjetees.map(m => (
            <View key={m.mois} style={s.carte}>
              <Text style={s.moisTxt}>{libelleMois(m.mois, t)}</Text>
              <View style={{ flex: 1 }} />
              <Text style={[s.prevuTxt, m.prevu < 0 && { color: UI.danger || '#c0392b' },
                m.prevu > 0 && { color: UI.ok || '#2e7d32' }]}>
                {m.prevu > 0 ? '+' : ''}{formatEuro(m.prevu)}
              </Text>
              <Text style={[s.cumuleTxt, m.cumule < 0 && { color: UI.danger || '#c0392b' }]}>
                {formatEuro(m.cumule)}
              </Text>
            </View>
          ))
        )}
      </ScrollView>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  totalCarte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 12 },
  totalLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  totalVal: { fontSize: 21, fontWeight: '800', color: UI.ink, marginTop: 2 },
  dureeLigne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  dureeLbl: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 13, marginBottom: 8 },
  moisTxt: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  prevuTxt: { fontSize: 13, fontWeight: '700', color: UI.muted, minWidth: 80, textAlign: 'right' },
  cumuleTxt: { fontSize: 14.5, fontWeight: '800', color: UI.ink, minWidth: 90, textAlign: 'right' },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
